import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import type { UseFormGetValues } from 'react-hook-form';

import { advisorsApi } from '@/api/waterlily';
import { fetchSessionForAuthRequest, selectSessionAdvisor } from '@/model';
import { setCachedFilterDefaults } from '../lib/filter-defaults-cache';
import { useHiddenCarriers } from '../stores/carrier-exclusions-store';
import { useSelectedBenefitPaymentType } from '../stores/benefit-type-filter-store';
import { useBenefitDesignInputs } from '../stores/benefit-design-inputs-store';
import {
  useSelectedEligibilityFilters,
  useSelectedFeatureBenefitsFilters,
} from '../stores/eligibility-filters-store';
import type { PaymentOption, ShopInsuranceMode } from '../types';

interface UseShopInsuranceFilterDefaultsProps {
  mode: ShopInsuranceMode;
  paymentOption: PaymentOption;
  getValues: UseFormGetValues<any>;
}

export function useShopInsuranceFilterDefaults({
  mode,
  paymentOption,
  getValues,
}: UseShopInsuranceFilterDefaultsProps) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);

  const hiddenCarriers = useHiddenCarriers();
  const selectedBenefitPaymentType = useSelectedBenefitPaymentType();
  const benefitDesignInputs = useBenefitDesignInputs();
  const selectedEligibilityFilters = useSelectedEligibilityFilters();
  const selectedFeatureBenefitsFilters = useSelectedFeatureBenefitsFilters();

  const saveMutation = useMutation({
    mutationFn: (defaults: ShopInsuranceFilterDefaults) =>
      advisorsApi.saveShopInsuranceFilterDefaults(advisor.advisorId, defaults),
    onSuccess: (_data, defaults) => {
      setCachedFilterDefaults(advisor.advisorId, defaults);
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
    },
  });

  const saveCurrentAsDefaults = useCallback(() => {
    const formValues = getValues();
    const defaults: ShopInsuranceFilterDefaults = {
      premium: formValues.premium,
      useCustomPremiumInput: formValues.useCustomPremiumInput ?? false,
      hiddenCarriers,
      benefitPaymentType: selectedBenefitPaymentType,
      eligibilityFilters: selectedEligibilityFilters,
      featureBenefitsFilters: selectedFeatureBenefitsFilters,
      benefitDuration: formValues.benefitDuration,
      inflationProtection: formValues.inflationProtection,
      inflationType: formValues.inflationTypeFilter,
      paymentOption,
      mode,
      benefitDesignInputs,
    };
    saveMutation.mutate(defaults);
  }, [
    getValues,
    hiddenCarriers,
    selectedBenefitPaymentType,
    benefitDesignInputs,
    selectedEligibilityFilters,
    selectedFeatureBenefitsFilters,
    paymentOption,
    mode,
    saveMutation,
  ]);

  return {
    isSaving: saveMutation.isPending,
    isSuccess: saveMutation.isSuccess,
    saveCurrentAsDefaults,
  };
}
